/** @format */

import type { QueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { raceQueryOptions, standingsQueryOptions } from "@/api/racesApi";

/**
 * Shared year validation schema for route search params
 * Validates 4-digit years between 2000 and 2100
 */
export const yearSearchSchema = z.object({
  year: z
    .union([z.string(), z.number()])
    .refine(val => {
      const yearStr = String(val);
      return /^\d{4}$/.test(yearStr);
    }, "Year must be a 4-digit number")
    .refine(val => {
      const year = typeof val === "number" ? val : parseInt(val, 10);
      return year >= 2000 && year <= 2100;
    }, "Year must be between 2000 and 2100")
    .optional(),
});

/**
 * Fetches race data and extracts series and year for dependent queries
 * Uses query options factory from racesApi for single source of truth
 * @param queryClient - React Query client
 * @param raceId - Race ID to fetch
 * @returns Object with race data, seriesId, and year
 */
export async function fetchRaceData(
  queryClient: QueryClient,
  raceId: string
): Promise<{ race: Race; seriesId: string; year: string }> {
  // Use factory from racesApi instead of inline queryFn
  const race = await queryClient.ensureQueryData(raceQueryOptions.detail(raceId));

  const seriesId = race?.seriesNid?.toString() ?? "1";
  const year = race?.raceDate
    ? new Date(race.raceDate).getFullYear().toString()
    : new Date().getFullYear().toString();

  return { race, seriesId, year };
}

/**
 * Fetches races list by series and year
 * Uses query options factory from racesApi for single source of truth
 * @param queryClient - React Query client
 * @param series - Series ID
 * @param year - Year string
 */
export async function fetchRacesBySeriesAndYear(
  queryClient: QueryClient,
  series: string,
  year: string
): Promise<void> {
  // Use factory from racesApi - no more duplication!
  await queryClient.ensureQueryData(raceQueryOptions.list(series, year));
}

/**
 * Fetches standings list by series and year
 * Uses query options factory from racesApi for single source of truth
 * @param queryClient - React Query client
 * @param series - Series ID
 * @param year - Year string
 */
export async function fetchStandingsBySeriesAndYear(
  queryClient: QueryClient,
  series: string,
  year: string
): Promise<void> {
  // Validate series ID before fetching
  const validSeriesIds = ["1", "2", "3", "9999"];
  if (!validSeriesIds.includes(series)) {
    console.warn(`Invalid series ID: ${series}. Skipping standings fetch.`);
    return;
  }

  // Use factory from racesApi - no more duplication!
  // This will handle 404s gracefully and return empty array
  await queryClient.ensureQueryData(standingsQueryOptions.season(series, year));
}
